import { Toast } from '@fattureincloud/fic-design-system'
import React, { FC, useEffect, useRef, useState } from 'react'

import { httpRequest } from '../../common/network'
import { errorMessage } from '../../locales/locale'
import { useAppSelector } from '../../redux/hooks'
import BreadCrumbs from '../BreadCrumbs/BreadCrumbs'
import Chat from '../Chat'
import {
  SectionContainer,
  SectionList,
  SectionLoadingImpl,
} from '../common.components'
import { MainContainer } from '../customComponents'
import { ArticleInfo, Path, SectionInfo, TreeItem } from '../definitions'
import { SectionType } from '../DnDComponents/dnd.components'
import SectionEditable from '../DnDComponents/SectionEditable'
import EditorModeToggle from '../EditorMode/EditorModeToggle'
import ElementNotFound from '../ElementNotFound'
import HelpHeaderDetailed from '../Header/HelpHeaderDetailed'
import PrivacyMessage from '../PrivacyMessage'
import { SectionProps } from './definitions'
import Subsection from './Subsection'

const Section: FC<SectionProps> = (props: SectionProps) => {
  const [section, setSection] = useState<SectionInfo | undefined>(undefined)
  const [subsections, setSubsections] = useState<SectionInfo[]>([])
  const [articles, setArticles] = useState<ArticleInfo[]>([])
  const [elementNotFound, setElementNotFound] = useState<boolean>(false)
  const [tree, setTree] = useState<TreeItem>()
  const [loadingSubsections, setLoadingSubsections] = useState<boolean>(false)

  const ref = useRef<HTMLDivElement>()
  const editorModeActive = useAppSelector((state) => state.editor.value)
  const realm = useAppSelector((state) => state.realm.value)
  const adminMode = useAppSelector((state) => state.adminMode.value)
  const visualizationMode = useAppSelector(
    (state) => state.visualizationMode.value
  )

  useEffect(() => {
    if (props.isExternal && ref.current) {
      ref.current.scrollTop = 0
    }
  }, [])

  useEffect(() => {
    loadData(props.searchKey)
  }, [props.searchKey, editorModeActive, adminMode])

  const loadData = (id: string) => {
    setLoadingSubsections(true)
    const searchedId = id || props.searchKey
    httpRequest({
      method: 'GET',
      path: 'help/list/' + searchedId,
      params: {
        realm: realm,
        visualize_mode: adminMode ? visualizationMode : 1,
      },
      onSuccess: (response: {
        data: {
          articles: ArticleInfo[]
          section: SectionInfo
          tree: TreeItem
          subSections: SectionInfo[]
        }
      }) => {
        setElementNotFound(!response.data.section)
        setSection(response.data.section)
        setArticles(response.data.articles)
        setSubsections(response.data.subSections)
        setTree(response.data.tree)
        setLoadingSubsections(false)
      },
      onError: (res) => Toast.error(errorMessage, { content: res.message }),
    })
  }

  const reloadSection = () => {
    loadData(props.searchKey)
  }

  const getBreadCrumbs = () => {
    const breadCrumbPaths: Path[] = []
    let c = 2
    while (c >= 1) {
      if (tree && tree['section' + String(c) + '_permalink']) {
        const permalink = tree['section' + String(c) + '_permalink']
        breadCrumbPaths.push({
          id: tree['section' + String(c) + '_id'],
          text: tree['section' + String(c) + '_title'],
          onClick: () => props.changeView(permalink, 'sezione'),
        })
      }
      c--
    }
    if (section) {
      breadCrumbPaths.push({ id: String(section.id), text: section.title })
    }
    return breadCrumbPaths
  }

  const updateSection = (section: SectionInfo, type: SectionType) => {
    if (type === SectionType.Main) {
      setSection(section)
    } else if (type === SectionType.Subsection) {
      const index = subsections.findIndex((s) => s.id === section.id)
      const newSubsections = [
        ...subsections.slice(0, index),
        section,
        ...subsections.slice(index + 1),
      ]
      setSubsections(newSubsections)
    } else if (type === SectionType.SubSubSection) {
      const parent = section.parent_section
      const parentIndex = subsections.findIndex((s) => s.id === parent)
      const oldSubSubsections = subsections[parentIndex].subSections
      const subSubSectionIdx = oldSubSubsections.findIndex(
        (s) => s.id === section.id
      )
      const newSubSubsection = [
        ...oldSubSubsections.slice(0, subSubSectionIdx),
        section,
        ...oldSubSubsections.slice(subSubSectionIdx + 1),
      ]
      const newSubsections = [
        ...subsections.slice(0, parentIndex),
        { ...subsections[parentIndex], subSections: newSubSubsection },
        ...subsections.slice(parentIndex + 1),
      ]
      setSubsections(newSubsections)
    }
  }

  const sectionArticles = (
    <SectionContainer
      isExternal={props.isExternal}
      className={`sectionContainer ${props.embedded ? 'embeddedSection' : ''} `}
    >
      <SectionList>
        <>
          <h2>
            {editorModeActive && <>{section?.id} - </>}
            {section?.title}
          </h2>
          <ul>
            {articles.map((article, idx) => {
              return (
                <li
                  key={idx}
                  onClick={() =>
                    props.changeView(article.permalink, 'articolo')
                  }
                >
                  <span>
                    {editorModeActive && <>{article.id} - </>}
                    {article.title}
                  </span>
                </li>
              )
            })}
          </ul>
        </>
      </SectionList>
    </SectionContainer>
  )

  const showSectionDetails = (
    <>
      <HelpHeaderDetailed
        closeIcon={!props.embedded}
        onClickClose={props.onClickClose}
        onClick={() => props.changeView(undefined, 'main')}
        title={section ? section.title : ''}
        description={section?.description ? section.description : ''}
        isExternal={props.isExternal}
        embedded={props.embedded}
      />
      <BreadCrumbs
        onSelect={props.changeView}
        paths={getBreadCrumbs()}
        className={'breadCrumbs'}
        embedded={props.embedded}
        isExternal={props.isExternal}
      />
      {!section && elementNotFound && (
        <ElementNotFound type={'section'} onClick={props.changeView} />
      )}
      {loadingSubsections && <SectionLoadingImpl />}
      {section && !loadingSubsections && (
        <>
          {editorModeActive && (
            <SectionEditable
              section={section}
              articles={articles}
              subsections={subsections ? subsections : []}
              parent_section={Number(section.id)}
              updateSection={updateSection}
              reloadSection={reloadSection}
              changeView={props.changeView}
            />
          )}
          {!editorModeActive && (
            <>
              <EditorModeToggle />
              {section && articles && articles.length > 0 && sectionArticles}
              {subsections &&
                subsections.length > 0 &&
                subsections.map((subSection, idx) => {
                  return (
                    <Subsection
                      type={'section'}
                      key={idx}
                      isExternal={props.isExternal}
                      idx={idx}
                      embedded={props.embedded}
                      subSection={subSection}
                      changeView={props.changeView}
                    />
                  )
                })}
            </>
          )}
        </>
      )}
    </>
  )

  return (
    <MainContainer
      className={props.className}
      isExternal={props.isExternal}
      ref={ref}
    >
      {showSectionDetails}
      {!editorModeActive && !elementNotFound && section && (
        <>
          <Chat type={'section'} id={Number(section.id)} />
          <PrivacyMessage />
        </>
      )}
    </MainContainer>
  )
}

export default Section
